import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, MessageSquare, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

// Sends an SMS or email to a student through the centre's GHL (replies land in GHL conversations).
const MessageDialog = ({ open, onOpenChange, student, courseId, classDate, kind = 'manual', defaultChannel = 'sms', onSent }) => {
  const [channel, setChannel] = useState(defaultChannel);
  const [subject, setSubject] = useState('');
  const [text, setText] = useState('');
  const [templates, setTemplates] = useState(null);
  const [sending, setSending] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    if (!open || !student) return;
    setChannel(defaultChannel);
    setSubject('');
    setText('');
    setTemplates(null);
    if (kind !== 'absence') return;
    fetch(`${API_URL}/api/training/messages/preview`, {
      method: 'POST', headers, credentials: 'include',
      body: JSON.stringify({ lead_id: student.lead_id, course_id: courseId, class_date: classDate, kind: 'absence' }),
    })
      .then(r => (r.ok ? r.json() : null))
      .then(t => {
        if (!t) return;
        setTemplates(t);
        setSubject(t.email_subject);
        setText(defaultChannel === 'email' ? t.email : t.sms);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, student?.lead_id, kind, classDate]);

  const switchChannel = (c) => {
    setChannel(c);
    if (templates) setText(c === 'email' ? templates.email : templates.sms);
  };

  const send = async () => {
    if (!text.trim()) { toast.error('Write a message first'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/training/messages/send`, {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({
          lead_id: student.lead_id, enrollment_id: student.enrollment_id, course_id: courseId,
          class_date: classDate, channel, subject: channel === 'email' ? subject : undefined, message: text, kind,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`${channel === 'sms' ? 'Text' : 'Email'} sent to ${student.name}`);
        onOpenChange(false);
        onSent?.();
      } else {
        toast.error(body.detail || 'Message not sent');
      }
    } finally {
      setSending(false);
    }
  };

  if (!student) return null;
  const missing = channel === 'sms' ? !student.phone : !student.email;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Message {student.name}</DialogTitle>
          <DialogDescription>Sent from your centre's number / email in GHL. Replies go to your GHL inbox.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="grid grid-cols-2 gap-2">
            {[{ v: 'sms', label: 'Text', icon: MessageSquare, to: student.phone }, { v: 'email', label: 'Email', icon: Mail, to: student.email }].map(opt => {
              const Icon = opt.icon;
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => switchChannel(opt.v)}
                  className={`p-2.5 rounded-xl border-2 text-left transition-all ${channel === opt.v ? 'border-pink-400 bg-pink-50' : 'border-slate-200 hover:border-slate-300'}`}
                >
                  <span className="flex items-center gap-1.5 text-sm font-medium text-slate-800"><Icon className="w-4 h-4" /> {opt.label}</span>
                  <span className="block text-xs text-slate-500 truncate">{opt.to || 'Not on file'}</span>
                </button>
              );
            })}
          </div>
          {missing ? (
            <p className="text-sm text-amber-700 bg-amber-50 rounded-xl px-3 py-2">
              No {channel === 'sms' ? 'phone number' : 'email'} saved for this student.
            </p>
          ) : (
            <>
              {channel === 'email' && (
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className="rounded-xl" aria-label="Email subject" />
              )}
              <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={channel === 'email' ? 8 : 5} placeholder="Write your message…" className="rounded-xl" aria-label="Message" />
              {channel === 'sms' && <p className="text-xs text-slate-400 text-right">{text.length} characters</p>}
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="btn-primary rounded-xl" onClick={send} disabled={sending || missing}>
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MessageDialog;
